/**
 * Condition parsing for template editor
 * Handles parsing of condition syntax into structured data
 */

import { CONDITION_OPERATORS, PATH_PATTERN } from '../constants';

/**
 * Parse value from string
 * Handles: strings ("value", 'value'), numbers (123), booleans (true, false), null
 */
export const parseValue = (
  valueStr: string
): string | number | boolean | null | undefined => {
  // String: "value" or 'value'
  if (
    (valueStr.startsWith('"') && valueStr.endsWith('"')) ||
    (valueStr.startsWith("'") && valueStr.endsWith("'"))
  ) {
    return valueStr.slice(1, -1);
  }

  // Boolean
  if (valueStr === 'true') return true;
  if (valueStr === 'false') return false;

  // Null
  if (valueStr === 'null') return null;

  // Number
  const num = Number(valueStr);
  if (!isNaN(num)) return num;

  return undefined;
};

/**
 * Parse comparison expression (e.g., "== 'active'", "> 18", "!= null")
 */
export const parseComparison = (
  expression: string
): { operator: string; value: string | number | boolean | null } | null => {
  const trimmed = expression.trim();

  // Try each operator
  for (const [, operatorSymbol] of Object.entries(CONDITION_OPERATORS)) {
    const opIndex = trimmed.indexOf(operatorSymbol);
    if (opIndex === -1) continue;

    const operator = operatorSymbol;
    const valueStr = trimmed.substring(opIndex + operator.length).trim();

    // Parse value
    const value = parseValue(valueStr);
    if (value !== undefined) {
      return { operator, value };
    }
  }

  return null;
};

/**
 * Parse a condition string to extract components
 *
 * @example
 * parseCondition("{{#if @.status}}")  → { type: 'if', variablePath: 'status' }
 * parseCondition("{{#if @.age > 18}}") → { type: 'if', variablePath: 'age', operator: '>', compareValue: 18 }
 * parseCondition("{{#unless @.disabled}}") → { type: 'unless', variablePath: 'disabled' }
 */
export const parseCondition = (
  conditionString: string
): {
  type: 'if' | 'unless';
  variablePath: string;
  operator?: string;
  compareValue?: string | number | boolean | null;
} | null => {
  const ifMatch = conditionString.match(
    new RegExp(`\\{\\{#if\\s+@\\.(${PATH_PATTERN})(?:\\s+(.+?))?\\}\\}`)
  );

  if (ifMatch) {
    const variablePath = ifMatch[1];
    const comparison = ifMatch[2]?.trim();

    if (!comparison) {
      return { type: 'if', variablePath };
    }

    const parsed = parseComparison(comparison);
    if (parsed) {
      return {
        type: 'if',
        variablePath,
        operator: parsed.operator,
        compareValue: parsed.value,
      };
    }

    return { type: 'if', variablePath };
  }

  const unlessMatch = conditionString.match(
    new RegExp(`\\{\\{#unless\\s+@\\.(${PATH_PATTERN})\\}\\}`)
  );

  if (unlessMatch) {
    return {
      type: 'unless',
      variablePath: unlessMatch[1],
    };
  }

  return null;
};
